<template>
    <CalendarRoot
        v-slot="{ grid, weekDays }"
        :class="cn('w-60 px-3 py-2', props.class)"
        v-bind="forwarded"
    >
        <CalendarHeader>
            <CalendarHeading class="flex-1 pl-2" />
            <CalendarPrevButton />
            <CalendarNextButton />
        </CalendarHeader>

        <div class="mt-4 flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
            <CalendarGrid
                v-for="month in grid"
                :key="month.value.toString()"
            >
                <CalendarGridHead>
                    <CalendarGridRow>
                        <CalendarHeadCell
                            v-for="day in weekDays"
                            :key="day"
                        >
                            {{ day }}
                        </CalendarHeadCell>
                    </CalendarGridRow>
                </CalendarGridHead>
                <CalendarGridBody>
                    <CalendarGridRow
                        v-for="(weekDates, index) in month.rows"
                        :key="`weekDate-${index}`"
                        class="mt-2 w-full"
                    >
                        <CalendarCell
                            v-for="weekDate in weekDates"
                            :key="weekDate.toString()"
                            :date="weekDate"
                        >
                            <CalendarCellTrigger
                                :day="weekDate"
                                :month="month.value"
                            />
                        </CalendarCell>
                    </CalendarGridRow>
                </CalendarGridBody>
            </CalendarGrid>
        </div>
    </CalendarRoot>
</template>

<script lang="ts" setup>
import { cn } from '@/lib/utils';
import { reactiveOmit } from '@vueuse/core';
import type { CalendarRootEmits, CalendarRootProps } from 'reka-ui';
import { CalendarRoot, useForwardPropsEmits } from 'reka-ui';
import type { HTMLAttributes } from 'vue';

import CalendarCell from '@/components/design-system/form/date-picker/CalendarCell.vue';
import CalendarCellTrigger from '@/components/design-system/form/date-picker/CalendarCellTrigger.vue';
import CalendarGrid from '@/components/design-system/form/date-picker/CalendarGrid.vue';
import CalendarGridBody from '@/components/design-system/form/date-picker/CalendarGridBody.vue';
import CalendarGridHead from '@/components/design-system/form/date-picker/CalendarGridHead.vue';
import CalendarGridRow from '@/components/design-system/form/date-picker/CalendarGridRow.vue';
import CalendarHeadCell from '@/components/design-system/form/date-picker/CalendarHeadCell.vue';
import CalendarHeader from '@/components/design-system/form/date-picker/CalendarHeader.vue';
import CalendarHeading from '@/components/design-system/form/date-picker/CalendarHeading.vue';
import CalendarNextButton from '@/components/design-system/form/date-picker/CalendarNextButton.vue';
import CalendarPrevButton from '@/components/design-system/form/date-picker/CalendarPrevButton.vue';

const props = defineProps<CalendarRootProps & { class?: HTMLAttributes['class'] }>();

const emits = defineEmits<CalendarRootEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>
